import { EzClass } from '/ezlibrary/EzClass.js';

import {
    EzObject,
    EzBoolean,
    EzString,
    EzArray,
    EzFunction,
    EzUrl,
    EzJson,
    EzPromise
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzRegistrationState
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzVersionChecker } from '/ezlibrary/EzVersionChecker.js';

import { EzHttpHelper } from '/public/javascript/common/ezclocker-http-helper.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides website navigation utility methods
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzNavigation } from '/public/javascript/common/ezclocker-navigation-helper.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen for ready event:
 *     globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen for ready event:
 *     EzClass.ezWaitReady(
 *         EzNavigation.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access singleton instance:
 *      Within EzmNavigator: EzNavigation.ezInstance
 *      Outside of EzmNavigator: globalThis.ezApi.ezclocker.ezNavigation
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzNavigation extends EzClass {
    /**
     * @public @method @static @readonly @property
     * Gets string const '[EZPV_employerId]'
     * @returns {string}
     */
    static get EZPathVar_employerId() {
        return '[EZPV_employerId]';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '[EZPV_employeeId]'
     * @returns {string}
     */
    static get EZPathVar_employeeId() {
        return '[EZPV_employeeId]';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '[EZPV_userId]'
     * @returns {string}
     */
    static get EZPathVar_userId() {
        return '[EZPV_userId]';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '[EZPV_id]'
     * @returns {string}
     */
    static get EZPathVar_id() {
        return '[EZPV_id]';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/resources/j_spring_security_check'
     * @returns {string}
     */
    static get SIGN_IN_PATH() {
        return '/resources/j_spring_security_check';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/resources/j_spring_security_logout'
     * @returns {string}
     */
    static get SIGN_OUT_PATH() {
        return '/resources/j_spring_security_logout';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/mobile/public'
     * @returns {string}
     */
    static get MOBILE_PUBLIC_PATH() {
        return '/mobile/public';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/mobile/secure'
     * @returns {string}
     */
    static get MOBILE_SECURE_PATH() {
        return '/mobile/secure';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/m'
     * @returns {string}
     */
    static get NEW_MOBILE_PATH() {
        return '/m';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const `${EzNavigation.NEW_MOBILE_PATH}/p`
     * @returns {string}
     */
    static get NEW_MOBILE_PUBLIC_PATH() {
        return `${EzNavigation.NEW_MOBILE_PATH}/p`;
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const `${EzNavigation.NEW_MOBILE_PATH}/s`
     * @returns {string}
     */
    static get NEW_MOBILE_SECURE_PATH() {
        return `${EzNavigation.NEW_MOBILE_PATH}/s`;
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/secure'
     * @returns {string}
     */
    static get WEBSITE_SECURE_PATH() {
        return '/secure';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '/public'
     * @returns {string}
     */
    static get WEBSITE_PUBLIC_PATH() {
        return '/public';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '_api'
     * @returns {string}
     */
    static get INTERNAL_API_PATH_PREFIX() {
        return '_api';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const '_'
     * @returns {string}
     */
    static get INTERNAL_CLASSIC_API_PATH_PREFIX() {
        return '_';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const 'api'
     * @returns {string}
     */
    static get PUBLIC_API_PATH_PREFIX() {
        return 'api';
    }

    /**
     * @public @method @static @readonly @property
     * Gets string const 'v1'
     * @returns {string}
     */
    static get DEFAULT_INTERNAL_API_VERSION() {
        return 'v1';
    }

    /**
     * @public @readonly @property
     * Gets string const revision value
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to: EzVersionChecker.ezWebsiteVersion
     */
    get revisionVersion() {
        return EzVersionChecker.ezWebsiteVersion;
    }

    /**
     * @private @field
     * Stores the value of isDeveloperDocs boolean
     * Default: false
     * @type {boolean}
     */
    #isDeveloperDocs = false;
    /**
     * @public @property @getter
     * Gets the value of isDeveloperDocs boolean
     * @returns {boolean}
     */
    get isDeveloperDocs() {
        return EzBoolean.booleanOrFalse(this.#isDeveloperDocs);
    }
    /**
     * @public @property @getter
     * Sets the value of isDeveloperDocs boolean
     * @param {boolean} isDeveloperDocs
     * Default: false
     */
    set isDeveloperDocs(isDeveloperDocs = false) {
        this.#isDeveloperDocs = EzBoolean.booleanOrFalse(isDeveloperDocs);
    }

    /**
     * @private @field
     * Stores if the file version is enabled or not
     * Default: true
     * @type {boolean}
     */
    #enableFileRevision = true;
    /**
     * @public @property @getter
     * Gets if the file version is enabled or not
     * @returns {boolean}
     */
    get enableFileRevision() {
        return EzBoolean.booleanOrTrue(this.#enableFileRevision);
    }
    /**
     * @public @property @getter
     * Sets if the file version is enabled or not
     * @param {boolean} isDeveloperDocs
     * Default: true
     */
    set enableFileRevision(enableFileRevision = true) {
        this.#enableFileRevision = EzBoolean.booleanOrTrue(enableFileRevision);
    }

    /**
     * @private @field
     * Stores the currently configured environment id (lowercase)
     * Default: 'prd'
     * @type {string}
     */
    #ezEnvironment = 'prd';
    /**
     * @public @method @getter @property
     * Gets the currently configured environment id (lowercase)
     * @returns {string}
     */
    get ezEnvironment() {
        return EzString.stringWithLengthOrDefault(
            this.#ezEnvironment,
            'prd');
    }
    /**
     * @public @method @setter @property
     * Sets the currently configured environment id (lowercase)
     * @param {string} environment
     * Default: 'prd'
     */
    set ezEnvironment(environment = 'prd') {
        this.#ezEnvironment = EzString.stringWithLengthOrDefault(
            environment,
            'prd');
    }

    static #initialized = false;
    static get initialized() {
        return this.#initialized;
    }
    static set initialized(initialized = false) {
        this.#initialized = EzBoolean.booleanOrFalse(initialized);
    }

    /**
     * @public @method
     * Initializes EzNavigation
     * @returns {EzNavigation}
     */
    ezInit() {
        if (!EzNavigation.initialized) {
            EzNavigation.ezInstance.ezLoadEnvironmentId();
        }

        return EzNavigation.ezInstance;
    }

    /**
     * @public @method
     * Obtains the current environment
     * @returns {Promise.resolve}
     * Resolve contains the lower case environment id
     */
    ezLoadEnvironmentId() {
        return EzPromise.asyncAction(
            (finished) => globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                EzNavigation.ezInstance.ezGetPublicApiUrl('version', 'v1'))
                .then(
                    globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                    globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                .then(
                    (response) => {
                        if (EzString.hasLength(response.environment)) {
                            EzNavigation.ezInstance.ezEnvironment = response.environment.toLowerCase();
                        }

                        if (!EzString.hasLength(EzNavigation.ezInstance.ezEnvironment) && EzString.hasLength(response.targetEnvironment)) {
                            EzNavigation.ezInstance.ezEnvironment = response.targetEnvironment.toLowerCase();
                        }

                        if (!EzString.hasLength(EzNavigation.ezInstance.ezEnvironment)) {
                            EzNavigation.ezInstance.ezEnvironment = globalThis.ezApi.ezclocker.ezUrlHelper.ezGetEnvironmentFromUrl().toLowerCase();
                        }

                        if (!EzString.hasLength(EzNavigation.ezInstance.ezEnvironment)) {
                            EzNavigation.ezInstance.ezEnvironment = 'prd';
                        }

                        return finished(EzNavigation.ezInstance.ezEnviornment);
                    },
                    (eResponse) => {
                        globalThis.ezApi.ezclocker.ezLogger.warn(
                            EzString.em`
                            EzNavigation: Environment information was not available. Defaulting to enviornment PRD
                            Error response: ${globalThis.ezApi.ezToJson(eResponse)}`);

                        EzNavigation.ezInstance.ezEnviornment = 'prd';

                        return finished(EzNavigation.ezInstance.ezEnviornment);
                    }));
    }

    /**
     * @public @method
     * Returns the ezClocker base url
     * @returns {string}
     */
    ezGetBaseUrl() {
        const baseUrl = window.location.origin;

        const pathItemsArray = EzString.stringOrEmpty(window.location.pathname).split('/');

        return 2 <= pathItemsArray.length && 'ezclocker' === pathItemsArray[1].toLowerCase()
            ? `${baseUrl}/${pathItemsArray[1]}`
            : baseUrl;
    }

    /**
     * @public @function
     * Updates the search value in the window.navigation.search property
     * @param {undefined|null|string} newSearchValue
     * Do not include the leading '?'
     * @returns {string}
     * Updated windows.location.href value
     */
    ezSetWindowNavigatioNSearchValue(newSearchValue = EzString.EMPTY) {
        if (EzString.hasText(newSearchValue)) {
            window.history.pushState(
                {},
                document.title,
                newSearchValue.startsWith('?')
                    ? `${window.location.origin}${window.location.pathname}${newSearchValue}`
                    : `${window.location.origin}${window.location.pathname}?${newSearchValue}`);
        } else {
            window.history.pushState(
                {},
                document.title,
                `${window.location.origin}${window.location.pathname}`);
        }

        return window.location.href;
    }

    /**
     * @public @method
     * Returns the URL in the current window.location.origin
     */
    ezGetLocationUrl() {
        return window.location.href.toString();
    }

    /**
     * @public @method
     * Builds a url and then applies the file revision param.
     * @param {string} baseUrl
     * @param {array} pathParts
     * Default: empty array
     * @param {undefined|null|boolean} noFileRevision
     * Default: false
     */
    ezBuildUrl(baseUrl = '/', pathParts = [], noFileRevision = false) {
        let path = EzUrl.postSlash(
            EzString.stringOrEmpty(baseUrl));

        if (EzArray.hasLength(pathParts)) {
            for (const pathPart of pathParts) {
                if (EzString.hasLength(pathPart)) {
                    if (path.endsWith('/') && pathPart.startsWith('/')) {
                        // Remove / from pathPart as it already exists in path ending
                        path = `${path}${pathPart.substr(1)}`;
                    } else if (!path.endsWith('/') && !pathPart.startsWith('/')) {
                        // Add the missing path separator between path and pathPart
                        path = `${path}/${pathPart}`;
                    } else {
                        // Path separator should be good to go
                        path = `${path}${pathPart}`;
                    }
                }
            }
        }

        return EzBoolean.booleanOrFalse(noFileRevision)
            ? path
            : EzNavigation.ezInstance.ezApplyFileRevision(path);
    }

    /**
     * @public @method
     * Returns the url for a dynamic page. Example: https://ezclocker.com/dynamic/{pagename}
     * @param {string} pageName
     * @returns {string}
     */
    ezGetDynamicPageUrl(pageName) {
        return globalThis.ezApi.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            ['dynamic', pageName]);
    }

    /**
     * @param {string} urlSnip
     * @public @method
     */
    ezGetUrlFromBase(urlSnip) {
        return globalThis.ezApi.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [urlSnip]);
    }

    /**
     * @public @method
     * Returns the environment the website is running within
     * @returns {Promise.resolve}
     */
    ezGetEnvironment() {
        if (EzString.hasLength(EzNavigation.ezInstance.ezEnvironment)) {
            return EzPromise.resolve(EzNavigation.ezInstance.environment);
        }

        return EzPromise.asyncAction(
            (finished) => {
                globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                    EzNavigation.ezInstance.ezBuildUrl(
                        EzNavigation.ezInstance.ezGetBaseUrl(),
                        ['environment']))
                    .then(
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                    .then(
                        (response) => {
                            EzNavigation.ezInstance.environment = response.message;

                            return finished(EzNavigation.ezInstance.environment);
                        },
                        (eResponse) => {
                            globalThis.ezApi.ezclocker.ezLogger.error(
                                EzString.em`
                                    Failed to determine the current environment.
                                    [Error response: ${globalThis.ezApi.ezclocker.ezHttpHelper.extractErrorResponseMessage(eResponse)}]`);

                            return finished('PRD');
                        });
            });
    }

    /**
     * @public @method
     * Navigates to the provided URL
     * @param {string} url
     * @returns {window.location}
     */
    ezNavigateTo(url, skipRevision) {
        const urlToUse = EzBoolean.booleanOrFalse(skipRevision)
            ? url
            : EzNavigation.ezInstance.ezApplyFileRevision(url);

        if (EzFunction.isFunction(window.location.assign)) {
            return window.location.assign(urlToUse);
        }

        window.location = urlToUse;

        return window.location;
    }

    /**
     * @public @method
     * If website revsion version is enabled, the url is returned with the file revision param appended:
     *      Template for URL without params........: `${url}?${EzVersionChecker.ezWebsiteRevisionVersion}`
     *      Template for URL with existing params..: `${url}&${EzVersionChecker.ezWebsiteRevisionVersion}`
     * Otherwise, the url is returned as provided.
     * @param {undefined|null|string} url
     * @returns {string}
     */
    ezApplyFileRevision(url) {
        if (!EzString.hasLength(url)) {
            // no url to file rev
            return url;
        }

        if (EzBoolean.isFalse(EzNavigation.ezInstance.enableFileRevision) || !EzString.hasText(EzVersionChecker.ezWebsiteRevisionVersion) ||
            -1 !== url.indexOf(EzVersionChecker.ezWebsiteRevisionVersion)) {
            return url;
        }

        return -1 !== url.indexOf('?')
            ? `${url}&${EzVersionChecker.ezWebsiteRevisionVersion}`
            : `${url}?${EzVersionChecker.ezWebsiteRevisionVersion}`;
    }

    /**
     * @public @method
     * @param {string} pageName
     * @returns {string}
     */
    ezGetSecurePageUrl(pageName) {
        return EzNavigation.ezInstance.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [
                EzNavigation.WEBSITE_SECURE_PATH,
                pageName
            ]);
    }

    /**
     * @public @method
     * Builds a url for the websites public folder.
     * Result Template: {EzNavigation.ezInstance.ezGetBaseUrl()}/EzNavigation.ezInstance.WEBSITE_PUBLIC_PATH/{urlSuffix}
     * Example: https://ezclocker.com/public/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {string}
     */
    ezBuildPublicUrl(urlSuffix) {
        return EzNavigation.ezInstance.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [
                EzNavigation.WEBSITE_PUBLIC_PATH,
                urlSuffix
            ]);
    }

    /**
     * @public @method
     * @param {string} pageName
     * @returns {string}
     */
    ezGetPublicPageUrl(pageName) {
        return EzNavigation.ezInstance.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [
                EzNavigation.WEBSITE_PUBLIC_PATH,
                pageName
            ]);
    }

    /**
     * @public @method
     * Returns the full url for a page relative under the welcome url
     * @param {string} pageName
     * @returns {string}
     */
    ezGetWelcomePageUrl(pageName) {
        return EzNavigation.ezInstance.ezBuildUrl(EzNavigation.ezInstance.ezGetBaseUrl(), [
            EzNavigation.WEBSITE_WELCOME_PATH,
            pageName
        ]);
    }

    /**
     * @public @method
     * Navigates to a page relative to the welcome base url
     * @param {string} pageName
     */
    ezNavigateToWelcomePage(pageName) {
        EzNavigation.ezInstance.ezNavigateTo(EzNavigation.ezInstance.ezBuildUrl(EzNavigation.ezInstance.ezGetBaseUrl(), [
            EzNavigation.WEBSITE_WELCOME_PATH,
            pageName
        ]), false);
    }

    /**
     * Navigates the user to a root page or url
     */
    ezNavigateToRoot(subPath) {
        EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                [
                    EzString.EMPTY,
                    subPath
                ])
            , false);
    }

    /**
     * @public @method
     * @param {string} pathAndFileName
     * @returns {string}
     */
    ezGetNodeModulesFile(pathAndFileName) {
        return EzNavigation.ezInstance.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [
                '/node_modules/',
                pathAndFileName
            ]);
    }

    /**
     * @public @method
     * @param {string} pageName
     */
    ezNavigateToPublicPage(pageName) {
        EzNavigation.ezInstance.ezNavigateTo(EzNavigation.ezInstance.ezGetPublicPageUrl(pageName), false);
    }

    /**
     * @public @method
     * @param {string} pageName
     */
    ezNavigateToPublicMobilePage(pageName) {
        EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                [EzNavigation.MOBILE_PUBLIC_PATH, pageName]),
            false);
    }

    /**
     * @public @method
     * Navigates the browser to the 'new' public mobile page.
     * Url template used: {EzNavigation.ezInstance.getBaseUrl()}/{EzNavigation.ezInstance.NEW_MOBILE_PUBLIC_PATH}/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {window.location}
     */
    ezNavigateToNewMobilePublicPage(urlSuffix) {
        return EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                [
                    EzNavigation.NEW_MOBILE_PUBLIC_PATH,
                    urlSuffix
                ]));
    }

    /**
     * @public @method
     * Navigates the browser to the 'new' public mobile page.
     * Url template used: {EzNavigation.ezInstance.getBaseUrl()}/{EzNavigation.ezInstance.NEW_MOBILE_PUBLIC_PATH}/{urlSuffix}
     * @param {string} urlSuffix
     * @returns {window.location}
     */
    ezNavigateToNewMobileSecurePage(urlSuffix) {
        return EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                [
                    EzNavigation.NEW_MOBILE_SECURE_PATH,
                    urlSuffix
                ]));
    }

    /**
     * @public @method
     * Navigates to the new mobile root path of /m and appends the provided mobileUriSuffx.
     */
    ezNavigateToNewMobileUri(mobileUriSuffix) {
        return EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                [
                    EzNavigation.NEW_MOBILE_PATH,
                    mobileUriSuffix
                ]));
    }

    /**
     * @public @method
     * Returns the full url to the public mobile page
     * @param {string} pageName
     * @returns {string}
     */
    ezGetPublicMobilePageUrl(pageName) {
        return EzNavigation.ezInstance.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [
                EzNavigation.MOBILE_PUBLIC_PATH,
                pageName
            ]);
    }

    /**
     * @public @method
     * Removes the v= param from the provided url, partial url, or uri string.
     * @param {string} uri
     * @returns {string}
     * @deprecated
     * Migrate to EzNavigation.ezInstance.ezRemoveVersionParamFromUri(uri);
     */
    ezRemoveVersionFromUri(uri) {
        return EzNavigation.ezInstance.ezRemoveVersionParamFromUri(uri);
    }

    /**
     * @public @method
     * Removes all the version params from the provided URI
     * @param {string} uri
     * @returns {string}
     */
    ezRemoveVersionParamFromUri(uri) {
        if (!EzString.hasLength(uri) || -1 === uri.indexOf('v=')) {
            // Doesn't have a version param
            return uri;
        }

        // Split out params
        const urlAllUrlParams = uri.split('?');

        if (2 !== urlAllUrlParams.length) {
            return uri;
        }

        const url = urlAllUrlParams[0];

        const urlParams = urlAllUrlParams[1].split('&');

        if (0 !== urlParams.length) {
            let params = EzString.EMPTY;

            for (const param of urlParams) {
                if (0 !== param.indexOf('v=')) {
                    params = 0 === params.length
                        ? `?${param}`
                        : `${params}&${param}`;
                }
            }

            return `${url}${params}`;
        }

        return url;
    }

    /**
     * @public @method
     * Builds a url to the 'new' public mobile page.
     * Result Template: /m/p/{urlSuffix}
     * @param {string} urlSuffix
     */
    ezBuildNewMobilePublicPageUrl(urlSuffix) {
        return EzNavigation.ezInstance.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [
                EzNavigation.NEW_MOBILE_PUBLIC_PATH,
                EzNavigation.ezInstance.ezRemoveVersionParamFromUri(urlSuffix)
            ]);
    }

    /**
     * @public @method
     * @param {string} pageName
     */
    ezNavigateToSecureMobilePage(pageName) {
        EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                [
                    EzNavigation.MOBILE_SECURE_PATH,
                    pageName
                ]),
            false);
    }

    /**
     * @public @method
     * Displays the download mobile app page now, no mobile signin
     */
    ezNavigateToMobileSignIn() {
        const mobileSigninUri = 'm/p/download';

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(EzNavigation.ezInstance.ezGetSignOutUrl())
            .then(
                () => EzNavigation.ezInstance.ezNavigateToPublicMobilePage(mobileSigninUri),
                () => EzNavigation.ezInstance.ezNavigateToPublicMobilePage(mobileSigninUri));
    }

    /**
     * @public @method
     * @param {string|null} errorMessage
     * @param {string|null} userName
     */
    ezNavigateToSignIn(errorMessage, email, username) {
        const signInPage = EzUrl.build`
            signin/
            ?email=${EzString.stringWithTextOrEmpty(email)}
            &username=${EzString.stringWithTextOrEmpty(username)}
            &error=${EzString.stringWithTextOrEmpty(errorMessage)}`;

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(EzNavigation.ezInstance.ezGetSignOutUrl())
            .then(
                () => EzNavigation.ezInstance.ezNavigateToPublicPage(signInPage),
                (eResponse) => {
                    globalThis.ezApi.ezclocker.ezLogger.error(`Signout failure: ${EzJson.toJson(eResponse)}`);

                    EzNavigation.ezInstance.ezNavigateToPublicPage(signInPage);
                });
    }

    /**
     * @public @method
     * Navigates the user to the ezClocker Sign Up page
     * @param {undefined|null|string} email
     * @param {undefined|null|string} userName
     */
    ezNavigateToSignUp(email, userName) {
        EzNavigation.ezInstance.ezNavigateToPublicPage(
            EzUrl.build`
                signup.html
                ?email=${EzString.stringOrEmpty(email)}
                &username=${EzString.stringOrEmpty(userName)}`);
    }

    /**
     * @public @method
     * @param {string} url
     * @param {string} queryName
     * @param {string} queryValue
     * @returns {string}
     */
    ezAddQueryParam(url, queryName, queryValue) {
        const queryString = EzString.hasText(queryName)
            ? `${queryName}=${EzString.stringOrEmpty(queryValue)}`
            : EzString.EMPTY;

        return EzUrl.build`
           ${EzString.stringOrEmpty(url)}
           ${-1 === EzString.stringOrEmpty(url).indexOf('?') ? '?' : '&'}
           ${queryString}`;
    }

    /**
     * @public @method
     * Signs the currently logged in user out by navigating to the logout url.
     */
    ezSignOut() {
        EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezGetSignOutUrl(),
            false);
    }

    /**
     * @public @method
     * Navigates to the employer dashboard
     */
    ezNavigateToEmployerDashboard() {
        EzNavigation.ezInstance.ezNavigateToSecurePage('employer/');
    }

    /**
     * @public @method
     * Navigates to the employee dashboard
     */
    ezNavigateToEmployeeDashboard() {
        EzNavigation.ezInstance.ezNavigateToSecurePage('employeeDashboard.html');
    }

    /**
     * @public @method
     * Navigates to the employer schedule
     */
    ezNavigateToEmployerSchedules() {
        EzNavigation.ezInstance.ezNavigateToSecurePage('schedule/');
    }

    /**
     * @public @method
     * Navigates to the employer time-off page
     */
    ezNavigateToTimeOff() {
        EzNavigation.ezInstance.ezNavigateToSecurePage('time-off/');
    }

    /**
     * @public @method
     * Navigates to the employer schedule
     */
    ezNavigateToEmployeeSchedules() {
        EzNavigation.ezInstance.ezNavigateToSecurePage('es.html');
    }

    /**
     * @public @method
     * Navigates to the employee archive
     */
    ezNavigateToEmployeeArchive() {
        EzNavigation.ezInstance.ezNavigateToSecurePage('archive/');
    }

    /**
     * @public @method
     * Navigates to the Account page
     */
    ezNavigateToEmployerAccountPage(optionalParams) {
        if (EzString.hasLength(optionalParams)) {
            EzNavigation.ezInstance.ezNavigateToSecurePage(`account?${optionalParams}}`);
        } else {
            EzNavigation.ezInstance.ezNavigateToSecurePage('account/');
        }
    }

    /**
     * @public @method
     * Navigates to the Integrations page
     */
    ezNavigateToEmployerIntegrationsPage(optionalParams) {
        if (EzString.hasLength(optionalParams)) {
            let url = EzNavigation.ezInstance.ezGetSecurePageUrl('integrations')

            if (url.indexOf("?") > 0) {
                //remove cgi params
                url = url.substring(0, url.indexOf("?"));
            }

            EzNavigation.ezInstance.ezNavigateTo(
                EzUrl.build`
                    ${url}
                    ?${optionalParams}`,
                false);
        } else {
            EzNavigation.ezInstance.ezNavigateToSecurePage('integrations/');
        }
    }

    /**
     * @public @method
     * Navigates to the base url
     */
    ezNavigateToMain() {
        EzNavigation.ezInstance.ezNavigateTo(EzNavigation.ezInstance.ezGetBaseUrl());
    }

    /**
     * @public @method
     * Navigates to the public ezClocker landing page
     */
    ezNavigateToLandingPage() {
        EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                ['/welcome']),
            false);
    }

    /**
     * @public @method
     * Gets the ezclocker logout url
     * @returns {string}
     */
    ezGetSignOutUrl() {
        return EzNavigation.ezInstance.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [EzNavigation.SIGN_OUT_PATH]);
    }

    /**
     * @public @method
     * Obtains the sign-in url for ezClocker
     * @returns {string}
     */
    ezGetSigninUrl() {
        return EzNavigation.ezInstance.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [EzNavigation.SIGN_IN_PATH]);
    }

    /**
     * @public @method
     * Returns the full url for a internal classic api endpoint
     * @param {string} serviceName
     * Name of the service (without the underscore prefix)
     * @returns {string}
     */
    ezGetInternalClassicApiUrl(serviceName) {
        return EzBoolean.isTrue(EzNavigation.ezInstance.isDeveloperDocs)
            ? EzNavigation.ezInstance.ezGetPublicClassicApiUrl(serviceName)
            : globalThis.ezApi.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                [
                    `${EzNavigation.INTERNAL_CLASSIC_API_PATH_PREFIX}${serviceName}`
                ]);
    }

    /**
     * @public @method
     * Builds an internal api url. If apiVersion is not provided, the default api version is used.
     * Template: {domain}/_api/{apiVersion}/{urlSuffix}
     * @param {string} urlSuffix
     * @param {String|null} apiVersion
     * @returns {string}
     */
    ezBuildInternalApiUrl(urlSuffix, apiVersion) {
        return globalThis.ezApi.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [
                EzNavigation.INTERNAL_API_PATH_PREFIX,
                globalThis.ezApi.ezAssignOrDefault(apiVersion, EzNavigation.DEFAULT_INTERNAL_API_VERSION),
                urlSuffix
            ]);
    }

    /**
     * @public @method
     * Returns the url for an internal api
     * @param {string} serviceName
     * @param {string|null} version
     * @returns {string}
     */
    ezGetInternalApiUrl(serviceName, version) {
        if (!EzString.hasLength(version)) {
            version = EzNavigation.DEFAULT_INTERNAL_API_VERSION;
        }

        return EzNavigation.ezInstance.isDeveloperDocs
            ? EzNavigation.ezInstance.ezGetPublicApiUrl(serviceName, version)
            : globalThis.ezApi.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                [
                    EzNavigation.INTERNAL_API_PATH_PREFIX,
                    version,
                    serviceName
                ]);
    }

    /**
     * @public @method
     * @param {string} serviceName
     * @returns {string}
     */
    ezGetPublicClassicApiUrl(serviceName) {
        return globalThis.ezApi.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [serviceName]);
    }

    /**
     * @public @method
     * Obtains the full Url to an ezclocker public api
     * @param {string} serviceName
     * @param {string} version
     */
    ezGetPublicApiUrl(serviceName, version) {
        return globalThis.ezApi.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [
                EzNavigation.PUBLIC_API_PATH_PREFIX,
                EzString.hasLength(version)
                    ? version
                    : EzNavigation.DEFAULT_INTERNAL_API_VERSION,
                serviceName
            ]);
    }

    /**
     * @public @method
     * @param {string} subServiceName
     * @returns {string}
     */
    ezGetPublicAdminServiceUrl(subServiceName) {
        return !EzString.hasLength(subServiceName)
            ? globalThis.ezApi.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                ['/api/v1/admin'])
            : globalThis.ezApi.ezBuildUrl(
                EzNavigation.ezInstance.ezGetBaseUrl(),
                ['/api/v1/admin', subServiceName]);
    }

    /**
     * @public @method
     * Gets the full url to use for the provided imageSubPathAndName
     * @param {string} imageName
     * @returns {string}
     */
    ezGetPublicImagesUrl(imageSubPathAndName) {
        const imageBaseUrl = EzUrl.build`
            ${EzNavigation.ezInstance.ezGetStaticResourcesBaseUrl()}
            /images`;

        if (!EzString.hasLength(imageSubPathAndName)) {
            return EzNavigation.ezInstance.ezBuildUrl(imageBaseUrl);
        }

        imageSubPathAndName = imageSubPathAndName
            .replace('public/', EzString.EMPTY)
            .replace('images/', EzString.EMPTY);

        if ('/' !== imageSubPathAndName[0]) {
            imageSubPathAndName = `/${imageSubPathAndName}`;
        }

        return EzNavigation.ezInstance.ezBuildUrl(
            imageBaseUrl,
            [
                imageSubPathAndName
            ]);
    }

    /**
     * @public @method
     * Returns the static resources URL to use
     * @returns {string}
     */
    ezGetStaticResourcesBaseUrl() {
        switch (EzNavigation.ezInstance.ezEnvironment) {
            case 'dev':
                return -1 !== window.location.href.indexOf('dev2.ezclocker.com')
                    ? 'https://website.dev.ezclocker.com/dev2'
                    : `https://website.dev.ezclocker.com/${EzNavigation.ezInstance.ezEnvironment}`;
            case 'loc':
            case 'qal':
            case 'e2e':
            case 'prf':
                return `https://website.dev.ezclocker.com/${EzNavigation.ezInstance.ezEnvironment}`;
            case 'stg':
            case 'prd':
            default:
                return `https://website.prd.ezclocker.com/prd`;
        }
    }

    /**
     * @public @method
     * Navigates to the mobile site to the provided page
     * @param {string} url
     */
    ezRedirectToMobileSitePage(page) {
        if (!EzString.hasLength(page)) {
            // no where to navigate to
            return false;
        }

        if (EzObject.isValid(jQuery) && EzObject.isValid(jQuery.browser) && EzObject.isValid(jQuery.browser.mobile)) {
            EzNavigation.ezInstance.ezNavigateToPublicMobilePage(page);
            return true;
        }

        return false;
    }

    /**
     * @public @method
     * Uses the re-direct service to navigate a user to their dashboard
     * @param {boolean|null} mobile
     */
    ezRedirectToDashboard(mobile) {
        EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezGetUrlFromBase(
                EzBoolean.isTrue(mobile)
                    ? 'redirectToDashboard?mobile=true'
                    : 'redirectToDashboard'));
    }

    /**
     * @public @method
     * Navigates to a secure HTML page
     * @param {string} pageName
     */
    ezNavigateToSecurePage(pageName) {
        EzNavigation.ezInstance.ezNavigateTo(
            EzNavigation.ezInstance.ezGetSecurePageUrl(pageName),
            false);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToSecurePage()
     */
    navigateToSecurePage(pageName) {
        EzNavigation.ezInstance.ezNavigateToSecurePage(pageName);
    }


    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicAdminServiceUrl()
     */
    getPublicAdminServiceUrl(subserviceName) {
        return EzNavigation.ezInstance.ezGetPublicAdminServiceUrl(subserviceName);
    }

    /**
     * @public @method
     * Signs out the current user without notifications
     * @returns {promise}
     * @deprecated Migrate to globalThis.ezApi.ezclocker.ezAccountServices.ezSignOut()
     */
    ezSilentSignOut() {
        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(EzNavigation.ezInstance.ezGetSignOutUrl())
            .then(
                globalThis.ezApi.ezclocker.ezServices.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezServices.ezProcessApiReject);
    }

    /**
     * @public @method
     * Signs in a user using the user name and password without prompting them for input.
     * @param {string} username
     * @param {string} password
     * @param {function} success
     * @param {function} failure
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezAccountServices.ezSilentSignIn()
     */
    silentSignIn(username, password, success, failure) {
        globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
            globalThis.ezApi.ezUrlTemplate`
                ${EzNavigation.EZPathVar_employerId.SIGN_IN_PATH}
                    ?j_username=${globalThis.ezApi.ezEncode(username)}
                    &j_password=${globalThis.ezApi.ezEncode(password)}`)
            .then(
                (response, jqXHR) => globalThis.ezApi.ezCallback(success, response, jqXHR),
                (eResponse, jqXHR) => {
                    globalThis.ezApi.ezclocker.ezLogger.error(`Failed to silently sign in: ${globalThis.ezApi.ezToJson(eResponse)}`);
                    globalThis.ezApi.ezCallback(failure, eResponse, jqXHR);
                });
    }

    /**
     * @public @method
     * Signs in a user without notifications
     * @param {string} username
     * @param {string} password
     * @returns {promise}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezAccountServices.ezSilentSignIn()
     */
    ezSilentSignIn(username, password) {
        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
            globalThis.ezApi.ezUrlTemplate`
                ${EzNavigation.ezInstance.ezGetSigninUrl()}
                ?j_username=${username}
                &j_password=${password}`);
    }

    /**
     * @public @method
     * Returns a full url for the provided service path
     * @param {string} servicePath
     * @returns {string}
     * @deprecated
     * Migrate to:
     * EzApi.ezInstance.ezBuildUrl(
     *     EzNavigation.ezInstance.ezGetBaseUrl(),
     *     [servicePath]);
     */
    getServiceUrl(servicePath) {
        return globalThis.ezApi.ezBuildUrl(
            EzNavigation.ezInstance.ezGetBaseUrl(),
            [servicePath]);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetInternalClassicApiUrl()
     */
    getInternalClassicApiUrl(serviceName) {
        return EzNavigation.ezInstance.ezGetInternalClassicApiUrl(serviceName);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetInternalClassicApiUrl()
     */
    getInternalClassicServiceUrl(serviceName) {
        return EzNavigation.ezInstance.ezGetInternalClassicApiUrl(serviceName);
    }

    /**
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetInternalApiUrl()
     */
    getInternalApiUrl(serviceName, version) {
        return EzNavigation.ezInstance.ezGetInternalApiUrl(serviceName, version);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetInternalApiUrl()
     */
    ezGetInternalApiServiceUrl(serviceName, version) {
        return EzNavigation.ezInstance.ezGetInternalApiUrl(serviceName, version);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetInternalApiUrl()
     */
    getInternalApiServiceUrl(serviceName, version) {
        return EzNavigation.ezInstance.ezGetInternalApiUrl(serviceName, version);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicClassicApiUrl()
     */
    getPublicClassicServiceUrl(serviceName) {
        return EzNavigation.ezInstance.ezGetPublicClassicApiUrl(serviceName);
    }

    /**
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicClassicApiUrl()
     */
    ezGetPublicClassicServiceUrl(serviceName) {
        return EzNavigation.ezInstance.ezGetPublicClassicApiUrl(serviceName);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicClassicApiUrl()
     */
    getPublicClassicApiUrl(serviceName) {
        return EzNavigation.ezInstance.ezGetPublicClassicApiUrl(serviceName);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicApiUrl()
     */
    getPublicApiServiceUrl(path) {
        return EzNavigation.ezInstance.ezGetPublicApiUrl(path);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicApiUrl()
     */
    ezGetPublicApiServiceUrl(path) {
        return EzNavigation.ezInstance.ezGetPublicApiUrl(path);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicApiUrl()
     */
    getPublicApiUrl(path) {
        return EzNavigation.ezInstance.ezGetPublicApiUrl(path);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetUrlFromBase()
     */
    getUrlFromBase(path) {
        EzNavigation.ezInstance.ezGetUrlFromBase(path);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicImagesUrl()
     */
    getPublicImagesUrl(imageName) {
        return EzNavigation.ezInstance.ezGetPublicImagesUrl(imageName);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetEnvironment()
     */
    getEnvironment() {
        EzNavigation.ezInstance.ezGetEnvironment();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateTo()
     */
    navigateTo(path, skipRevision) {
        EzNavigation.ezInstance.ezNavigateTo(path, skipRevision);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetSecurePageUrl()
     */
    getSecurePageUrl(pageName) {
        EzNavigation.ezInstance.ezGetSecurePageUrl(pageName);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicPageUrl()
     */
    getPublicPageUrl(pageName) {
        EzNavigation.ezInstance.ezGetPublicPageUrl(pageName);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetNodeModulesFile()
     */
    getNodeModulesFile(pathAndFileName) {
        EzNavigation.ezInstance.ezGetNodeModulesFile(pathAndFileName);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToPublicPage()
     */
    navigateToPublicPage(path) {
        return EzNavigation.ezInstance.ezNavigateToPublicPage(path);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToPublicMobilePage()
     */
    navigateToPublicMobilePage(path) {
        return EzNavigation.ezInstance.ezNavigateToPublicMobilePage(path);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetPublicMobilePageUrl()
     */
    getPublicMobilePageUrl(path) {
        return EzNavigation.ezInstance.ezGetPublicMobilePageUrl(path);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToSecureMobilePage()
     */
    navigateToSecureMobilePage(path) {
        return EzNavigation.ezInstance.ezNavigateToSecureMobilePage(path);
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToMobileSignIn()
     */
    navigateToMobileSignIn() {
        return EzNavigation.ezInstance.ezNavigateToMobileSignIn();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToSignIn()
     */
    navigateToSignIn() {
        return EzNavigation.ezInstance.ezNavigateToSignIn();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezSignOut()
     */
    signOut() {
        return EzNavigation.ezInstance.ezSignOut();
    }

    /**
     * @public @method
     * @deprecated Migrate to globalThis.ezApi.ezclocker.ezAccountServices.ezSignOut()
     */
    silentSignOut() {
        return EzNavigation.ezInstance.ezSignOut();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToEmployerDashboard()
     */
    navigateToEmployerDashboard() {
        return EzNavigation.ezInstance.ezNavigateToEmployerDashboard();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToEmployeeDashboard()
     */
    navigateToEmployeeDashboard() {
        return EzNavigation.ezInstance.ezNavigateToEmployeeDashboard();
    }

    /**
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToEmployerSchedules()
     */
    navigateToEmployerSchedules() {
        return EzNavigation.ezInstance.ezNavigateToEmployerSchedules();
    }


    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToEmployeeArchive()
     */
    navigateToEmployeeArchive() {
        return EzNavigation.ezInstance.ezNavigateToEmployeeArchive();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToEmployerAccountPage()
     */
    navigateToEmployerAccountPage() {
        return EzNavigation.ezInstance.ezNavigateToEmployerAccountPage();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToMain()
     */
    navigateToMain() {
        return EzNavigation.ezInstance.ezNavigateToMain();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezNavigateToLandingPage()
     */
    navigateToLandingPage() {
        return EzNavigation.ezInstance.ezNavigateToLandingPage();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetBaseUrl()
     */
    getBaseUrl() {
        return EzNavigation.ezInstance.ezGetBaseUrl();
    }

    /**
     * @public @method
     * @deprecated
     * Migrate to EzNavigation.ezInstance.ezGetSignOutUrl()
     */
    ezGetLogoutUrl() {
        return EzNavigation.ezInstance.ezGetSignOutUrl();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetSignOutUrl()
     */
    getLogoutUrl() {
        return EzNavigation.ezInstance.ezGetSignOutUrl();
    }

    /**
     * @public @method
     * @deprecated Migrate to EzNavigation.ezInstance.ezGetSigninUrl()
     */
    getSigninUrl() {
        return EzNavigation.ezInstance.ezGetSigninUrl();
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezNavigation';
    }

    /**
     * @public @method @static @readonly @property
     * Gets the ezApi legacy short name for the singleton instance
     * @returns {string}
     */
    static get ezApiShortName() {
        return 'nav';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzNavigation_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with EzApi.
     * @type {EzNavigation}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]
        ? globalThis.ezApi.ezclocker[EzNavigation.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzNavigation}
     */
    static get ezInstance() {
        return EzNavigation.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     * @param {EzNavigation} instance
     */
    static set ezInstance(instance) {
        if (null != EzNavigation.#ezInstance) {
            throw new Error('EzNavigation\'s singleton instance is already reigstered with EzApi.');
        }

        EzNavigation.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]
        ? EzRegistrationState.REGISTERED
        : EzRegistrationState.NOT_REGISTERED;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzNavigation.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzNavigation.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES:
     *  Safe to ignore SonarLint hint javascript:S1541
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzNavigation.ezApiRegistrationState &&
            globalThis?.ezApi?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzHttpHelper.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzNavigation.ezInstance &&
            EzRegistrationState.ezIsRegisteredState(EzNavigation.ezApiRegistrationState);
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzNavigation.#ezCanRegister && !EzNavigation.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(
                EzNavigation,
                EzNavigation.ezApiName,
                false,
                [EzNavigation.ezApiShortName]);
        }

        return EzNavigation.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzNavigation.ezApiName
     *     2) Property getter EzNavigation.ezEventNames
     *     3) Property getter EzNavigation.ezInstance
     *     4) Property setter EzNavigation.ezInstance
     *     5) Property getter EzNavigation.ezApiRegistrationState
     *     6) Property setter EzNavigation.ezApiRegistrationState
     *     7) Property getter EzNavigation.#ezCanRegister
     *     8) Property getter EzNavigation.#ezIsRegistered
     *     9) Method EzNavigation.#ezRegistrator()
     */
    static {
        if (!EzNavigation.#ezIsRegistered) {
            EzNavigation.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzNavigation.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzNavigation.ezOnEzApiReadyEventName,
                    EzNavigation.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzHttpHelper.ezEventNames.onReady,
                    EzNavigation.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
